import { useState, useEffect } from 'react'
import { isHTTPError } from 'ky'
import { format } from 'date-fns'
import { UTCDate } from '@date-fns/utc'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { AlertCircle, Pencil } from 'lucide-react'
import { api } from '@frontend/lib/api'
import type { ClientListItem, ClientResponse } from '@common/types'
import type { UpdateClientDetailsInput } from '@common/schemas'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@frontend/components/ui/dialog'
import { Button } from '@frontend/components/ui/button'
import { Spinner } from '@frontend/components/ui/spinner'

interface UpdateDetailsModalProps {
  client: ClientListItem | null
  open: boolean
  onOpenChange: (open: boolean) => void
}

async function updateClientDetails(clientId: string, data: UpdateClientDetailsInput): Promise<ClientResponse> {
  return api.put(`clients/${clientId}`, { json: data }).json<ClientResponse>()
}

function formatDateForInput(dateString: string | null): string {
  if (!dateString) return ''
  // Parse as UTC to avoid timezone shift, format as yyyy-MM-dd for HTML date input
  const date = new UTCDate(dateString)
  return format(date, 'yyyy-MM-dd')
}

export function UpdateDetailsModal({ client, open, onOpenChange }: UpdateDetailsModalProps) {
  const queryClient = useQueryClient()

  // Form state - prepopulated from client
  const [name, setName] = useState('')
  const [contractStart, setContractStart] = useState('')
  const [contractEnd, setContractEnd] = useState('')
  const [keyExpiration, setKeyExpiration] = useState('')
  const [trustCenterEnabled, setTrustCenterEnabled] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Prepopulate form when modal opens or client changes
  useEffect(() => {
    if (open && client) {
      setName(client.name)
      setContractStart(formatDateForInput(client.contractStart))
      setContractEnd(formatDateForInput(client.contractEnd))
      setKeyExpiration(formatDateForInput(client.keyExpiration))
      setTrustCenterEnabled(client.trustCenterEnabled)
      setError(null)
    }
  }, [open, client])

  const updateMutation = useMutation({
    mutationFn: (data: UpdateClientDetailsInput) => updateClientDetails(client!.id, data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['clients-detailed'] })
      onOpenChange(false)
      toast.success(`Client "${data.client.name}" updated`)
    },
    onError: (err) => {
      if (isHTTPError(err)) {
        const data = err.data as { error?: string }
        setError(data.error || 'Failed to update client')
      } else {
        setError('Failed to update client')
      }
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    if (!client) return

    if (!name.trim()) {
      setError('Name is required')
      return
    }

    // Build update payload with only changed fields
    const updates: UpdateClientDetailsInput = {}

    if (name.trim() !== client.name) {
      updates.name = name.trim()
    }
    if (contractStart !== formatDateForInput(client.contractStart)) {
      updates.contractStart = contractStart
    }
    if (contractEnd !== formatDateForInput(client.contractEnd)) {
      updates.contractEnd = contractEnd || null
    }
    if (keyExpiration !== formatDateForInput(client.keyExpiration)) {
      updates.keyExpiration = keyExpiration || null
    }
    if (trustCenterEnabled !== client.trustCenterEnabled) {
      updates.trustCenterEnabled = trustCenterEnabled
    }

    // Only submit if there are changes
    if (Object.keys(updates).length === 0) {
      onOpenChange(false)
      return
    }

    updateMutation.mutate(updates)
  }

  const isSubmitting = updateMutation.isPending

  if (!client) return null

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Pencil className="h-5 w-5" />
            Update Client Details
          </DialogTitle>
          <DialogDescription>Update details for {client.name}</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Name */}
          <div>
            <label htmlFor="name" className="block text-sm font-medium mb-1">
              Client Name <span className="text-red-500">*</span>
            </label>
            <input
              id="name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="Enter client name"
              disabled={isSubmitting}
              autoComplete="off"
              data-1p-ignore
            />
          </div>

          {/* Contract Start */}
          <div>
            <label htmlFor="contractStart" className="block text-sm font-medium mb-1">
              Contract Start Date
            </label>
            <input
              id="contractStart"
              type="date"
              value={contractStart}
              onChange={(e) => setContractStart(e.target.value)}
              className="w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              disabled={isSubmitting}
            />
          </div>

          {/* Contract End */}
          <div>
            <label htmlFor="contractEnd" className="block text-sm font-medium mb-1">
              Contract End Date
            </label>
            <input
              id="contractEnd"
              type="date"
              value={contractEnd}
              onChange={(e) => setContractEnd(e.target.value)}
              className="w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              disabled={isSubmitting}
            />
            <p className="text-xs text-muted-foreground mt-1">Leave blank for ongoing contracts</p>
          </div>

          {/* Key Expiration */}
          {client.hasKey && (
            <div>
              <label htmlFor="keyExpiration" className="block text-sm font-medium mb-1">
                API Key Expiration Date
              </label>
              <input
                id="keyExpiration"
                type="date"
                value={keyExpiration}
                onChange={(e) => setKeyExpiration(e.target.value)}
                className="w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                disabled={isSubmitting}
              />
              <p className="text-xs text-muted-foreground mt-1">Update the expiration date for the existing API key</p>
            </div>
          )}

          {/* Trust Center */}
          <div>
            <label htmlFor="trustCenterEnabled" className="flex items-center gap-2 text-sm font-medium">
              <input
                id="trustCenterEnabled"
                type="checkbox"
                checked={trustCenterEnabled}
                onChange={(e) => setTrustCenterEnabled(e.target.checked)}
                className="h-4 w-4 rounded border focus:outline-none focus:ring-2 focus:ring-primary"
                disabled={isSubmitting}
              />
              Trust Center enabled
            </label>
            <p className="text-xs text-muted-foreground mt-1">
              The nightly sync clears this when Drata says the client's plan does not include the Trust Center. Tick it
              once they say they have turned it on.
            </p>
          </div>

          {/* Error display */}
          {error && (
            <div className="flex items-center gap-2 p-3 bg-destructive/10 text-destructive rounded-md text-sm">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              {error}
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <Spinner size="sm" className="mr-2" />
                  Saving...
                </>
              ) : (
                'Save Changes'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
